import { Event } from '@custom-types/activity'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'

/**
 * Handles reading events from the server for an activity source. The `next` method will retrieve
 * the next even from the source - the twist is that it's potentially an asynchronous call, so a
 * callback method must be supplied.
 */
export default class ActivitySourceEventsIterator {
  private readonly sourceName: string
  private readonly options: Options

  /** Flag indicating if events have been retrieved from the server */
  private eventsRetrieved: boolean

  /**
   * The last list of events retrieved from the server. As events are returned by `next`, they are
   * removed from the beginning of this array
   */
  private events: Event[] | null

  /**
   * The next before ID returned by the previous call to the server, used to retrieve the next
   * batch of events. If a call to the server returns null for `next_before_id`, then there are
   * no more events after this batch is exhausted.
   */
  private nextBeforeId: string | null

  /** Used to temporarily store the callback that should be passed */
  private nextCallback: (() => void) | null

  /** Once stop is called, the iterator will make no more calls to the callback function */
  private stopped: boolean

  /**
   * @param sourceName
   * @param opts The configuration options
   * @param opts.initialSourceEvents The set of events that may have already been supplied by the
   *  server.
   * @param opts.includePrivatelyVisibleEvents If set to `true, then events that are marked as
   *  "privatelyVisible" will be included in the stream. These show up when the user is viewing
   *  their own activity stream, and represent events that essentially record a user's activity
   *  that should be visible to them, but not to anyone else (things like unfollowing, unfavoriting,
   *  viewing, etc.)
   * @param opts.getMoreEventsCall function is called to get more events instead of the internal
   * getter
   */
  constructor(sourceName: string, opts?: Options) {
    this.sourceName = sourceName
    this.options = opts ?? {}
    this.eventsRetrieved = false
    this.events = null
    this.nextBeforeId = null
    this.nextCallback = null
    this.stopped = false

    // Store the initial set of events if they arrived with the source
    if (opts?.initialSourceEvents) {
      this.storeReceivedEvents(opts.initialSourceEvents)
    }
  }

  stop(): void {
    this.stopped = true
  }

  /**
   * Fetches the next event from the source. If there are no more events, then `null` is returned.
   */
  async next(): Promise<Event | null> {
    if (this.stopped) {
      return null
    }

    // initial call to server
    if (!this.eventsRetrieved) {
      return this.getMoreEvents()
    }

    if (this.events && this.events.length > 0) {
      const event = this.events.shift()

      // If this event is marked as "privatelyVisible" and we're set not to show them, then just
      // cycle back to get the next event
      if (event?.privatelyVisible && !this.options.includePrivatelyVisibleEvents) {
        return this.next()
      }

      // More buffered events remaining from last batch
      return event ?? null
    }

    // Buffered events exhausted, and no more events from server
    if (!this.nextBeforeId) {
      return null
    }

    return this.getMoreEvents()
  }

  /** Retrieves the next set of events from the server */
  async getMoreEvents(): Promise<Event | null> {
    const batchSize = 20
    let events: EventsResp | null = null
    if (typeof this.options.getMoreEventsCall === 'function') {
      events = await this.options.getMoreEventsCall(this.sourceName, batchSize, this.nextBeforeId)
    } else {
      const [resp] = await http.post<EventsResp>(ApiRoutes.activityStream.sourceEventsPath(), {
        source_name: this.sourceName,
        limit: batchSize,
        before_id: this.nextBeforeId,
      })

      if (resp?.data) {
        events = resp.data
      }
    }

    if (events) {
      this.storeReceivedEvents(events)
    }
    return this.next()
  }

  /** Called when an initial or new set of events arrives */
  private storeReceivedEvents(data: EventsResp): void {
    this.eventsRetrieved = true
    this.nextBeforeId = data.next_before_id
    this.events = data.before_events
  }
}

export type Options = {
  initialSourceEvents?: EventsResp
  includePrivatelyVisibleEvents?: boolean
  getMoreEventsCall?: (
    sourceName: string,
    batchSize: number,
    nextBeforeId: string | null
  ) => Promise<EventsResp>
}

export type EventsResp = {
  after_events: Event[]
  before_events: Event[]
  next_after_id: string | null
  next_before_id: string | null
  source_name: string
}
